import React, { useEffect, useMemo, useRef } from "react";
import { useKeyDown } from "../../hooks/useKeyDown";
import { BuyersTab, InfoTab, SellersTab, TemplatesTab } from "./case-setup-tabs";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Case, EMPTY_CASE } from "../../models/case";

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 7h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TABS = [
  { k: 0, label: "General", n: "01" },
  { k: 1, label: "Buyer", n: "02" },
  { k: 2, label: "Seller", n: "03" },
  { k: 3, label: "Templates", n: "04" },
];

function CaseSetupForm({ caseId, page }: { caseId: string; page: number }) {
  const navigate = useNavigate();
  const [caseObject, setCaseObject] = React.useState<Case | null>();
  const ref = useRef(null);

  const allEmails = useMemo(() => {
    let emails: string[] = [];
    if (caseObject?.buyers) {
      emails = emails.concat(caseObject.buyers.map((b) => b.email).filter(Boolean));
    }
    if (caseObject?.sellers) {
      emails = emails.concat(caseObject.sellers.map((s) => s.email).filter(Boolean));
    }
    for (const party of [caseObject?.sellersAgent, caseObject?.buyersAgent, caseObject?.lender, caseObject?.titleAbstractor, caseObject?.surveyor]) {
      if (!party) continue;
      if (party.email) emails.push(party.email);
      (party.additionalEmails || []).forEach((a: any) => { if (a.email) emails.push(a.email); });
    }
    return emails;
  }, [caseObject]);

  useKeyDown(() => {
    if (ref.current === document.activeElement) save();
  }, ["Enter"]);

  useEffect(() => {
    if (caseId) {
      if (caseId === "new") {
        setCaseObject(EMPTY_CASE);
      } else {
        fetchCase();
      }
    }
  }, [caseId]);

  const fetchCase = async () => {
    const snapshot = await getDoc(doc(getFirestore(), "cases", caseId));
    if (snapshot.exists()) {
      setCaseObject({ ...EMPTY_CASE, ...snapshot.data(), id: caseId } as Case);
    }
  };

  const save = async () => {
    if (caseId === "new") {
      await addDoc(collection(getFirestore(), "cases"), { ...caseObject, timeCreated: new Date(), allEmails });
      toast.success("Case setup complete!", { position: "top-right", autoClose: 5000, hideProgressBar: true, closeOnClick: true, pauseOnHover: false, draggable: false });
      navigate(`/case-form/new/0`);
      setCaseObject(EMPTY_CASE);
    } else {
      await updateDoc(doc(getFirestore(), "cases", caseId), { ...caseObject, allEmails });
      toast.success("Case updated!", { position: "top-right", autoClose: 5000, hideProgressBar: true, closeOnClick: true, pauseOnHover: false, draggable: false });
    }
  };

  if (!caseObject) return <div />;

  return (
    <div className="p-content p-setup-page">
      <div className="p-pagehead">
        <div>
          <div className="p-breadcrumb">Workspace · New Case</div>
          <h1 className="p-pagetitle">Case Setup</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="p-btn p-btn-ghost" onClick={() => navigate("/admin/cases")}>Cancel</button>
          <div tabIndex={0} ref={ref}>
            <button className="p-btn p-btn-primary" onClick={save}>
              Save &amp; complete <ArrowRight />
            </button>
          </div>
        </div>
      </div>

      <div className="p-card">
        <div className="p-tabs">
          {TABS.map((t) => (
            <div
              key={t.k}
              className={`p-tab${page === t.k ? " active" : ""}${t.k < page ? " done" : ""}`}
              onClick={() => navigate(`/case-form/${caseId}/${t.k}`)}
            >
              <span className="tabnum">{t.n}</span> {t.label}
            </div>
          ))}
        </div>

        <div style={{ padding: 32 }}>
          {page === 0 && <InfoTab caseObject={caseObject} setCaseObject={setCaseObject} showRequiredErrors={false} />}
          {page === 1 && <BuyersTab caseObject={caseObject} setCaseObject={setCaseObject} showRequiredErrors={false} />}
          {page === 2 && <SellersTab caseObject={caseObject} setCaseObject={setCaseObject} showRequiredErrors={false} />}
          {page === 3 && <TemplatesTab caseObject={caseObject} setCaseObject={setCaseObject} showRequiredErrors={false} />}
        </div>
      </div>
    </div>
  );
}

export default CaseSetupForm;
